import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ProspectDetailModal = ({ prospect, onClose, onUpdate }) => {
  const [notes, setNotes] = useState(prospect?.notes || []);
  const [newNote, setNewNote] = useState('');
  const [currentStatus, setCurrentStatus] = useState(prospect?.status);

  const statusOptions = [
    { value: 'demo-scheduled', label: 'Demo Scheduled' },
    { value: 'in-negotiation', label: 'In Negotiation' },
    { value: 'closed-won', label: 'Closed - Won' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleAddNote = () => {
    if (newNote?.trim()) {
      const updatedNotes = [
        ...notes,
        {
          id: Date.now(),
          content: newNote?.trim(),
          createdAt: new Date()?.toISOString(),
          createdBy: 'Admin User'
        }
      ];
      setNotes(updatedNotes);
      setNewNote('');
      onUpdate({ ...prospect, notes: updatedNotes });
    }
  };

  const handleStatusChange = (newStatus) => {
    setCurrentStatus(newStatus);
    onUpdate({
      ...prospect,
      status: newStatus,
      stageEntryDate: new Date()?.toISOString()
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1100 p-4">
      <div className="bg-background rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Image
              src={prospect?.avatar}
              alt={prospect?.avatarAlt}
              className="w-16 h-16 rounded-xl object-cover"
            />
            <div>
              <h2 className="text-2xl font-bold text-foreground">{prospect?.companyName}</h2>
              <p className="text-sm text-muted-foreground">{prospect?.contactName} • {prospect?.contactEmail}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={24} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={16} className="text-success" />
                <span className="text-xs text-muted-foreground">Deal Value</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(prospect?.dealValue)}</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Target" size={16} className="text-primary" />
                <span className="text-xs text-muted-foreground">Probability</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{prospect?.probability}%</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Users" size={16} className="text-accent" />
                <span className="text-xs text-muted-foreground">Team Size</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{prospect?.teamSize}</p>
            </div>
          </div>

          {/* Status Update */}
          <div className="bg-card rounded-xl border border-border p-4">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="TrendingUp" size={20} className="text-primary" />
              Update Status
            </h3>
            <Select
              options={statusOptions}
              value={currentStatus}
              onChange={handleStatusChange}
            />
          </div>

          {/* Company Details */}
          <div className="bg-card rounded-xl border border-border p-4">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Building2" size={20} className="text-primary" />
              Company Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-muted-foreground">Industry</span>
                <p className="text-sm font-medium text-foreground">{prospect?.industry}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">HR Budget</span>
                <p className="text-sm font-medium text-foreground">{prospect?.hrBudget}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Assigned To</span>
                <p className="text-sm font-medium text-foreground">{prospect?.assignedTo}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Stage Entry</span>
                <p className="text-sm font-medium text-foreground">{formatDate(prospect?.stageEntryDate)}</p>
              </div>
            </div>
          </div>

          {/* Next Action */}
          {prospect?.nextAction && (
            <div className="bg-primary/5 rounded-xl border border-primary/20 p-4">
              <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="Target" size={20} className="text-primary" />
                Next Action
              </h3>
              <p className="text-sm text-foreground">{prospect?.nextAction}</p>
            </div>
          )}

          {/* Admin Notes */}
          <div className="bg-card rounded-xl border border-border p-4">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="FileText" size={20} className="text-primary" />
              Admin Notes
            </h3>

            {/* Add Note */}
            <div className="mb-4">
              <textarea
                className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none mb-2"
                rows={3}
                placeholder="Add notes about conversations, demo details, follow-up actions..."
                value={newNote}
                onChange={(e) => setNewNote(e?.target?.value)}
              />
              <Button size="sm" onClick={handleAddNote} disabled={!newNote?.trim()}>
                Add Note
              </Button>
            </div>

            {/* Notes List */}
            <div className="space-y-3">
              {notes?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="FileText" size={48} className="text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No notes yet</p>
                </div>
              ) : (
                notes?.map((note) => (
                  <div key={note?.id} className="p-3 bg-background rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-foreground">{note?.createdBy}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(note?.createdAt)}</span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{note?.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background border-t border-border p-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProspectDetailModal;